import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
  Inspection,
  InspectionDetailNew,
  InspectionDetailUpdate,
  InspectionForSharedLock,
  InspectionList,
  InspectionUpdateLocation
} from "../../_objects/inspection";
import { CustomEncoder } from "../../_shared/custom-encoder";
import { HttpService } from "../http.service";
import { InspectionContractService } from "../interfaces/inspection-contract.service";
import { SubjectFilter } from "../storage.service";

@Injectable({
  providedIn: "root"
})
export class InspectionOnlineService extends HttpService implements InspectionContractService {
  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router
  ) {
    super();
    super.setCookieService(cookieService);
    super.setHttpClient(http);
    super.setRouter(router);
  }

  // IS01 GET /api/v1/inspections
  // page.indexは1はじまり
  getInspections(
    statusCode?: string,
    subjectFilter?: SubjectFilter,
    isOwn?: boolean,
    sort?: { active: string; direction: string },
    page?: { index: number; size: number }
  ): Observable<InspectionList> {
    const endpoint = super.getEndpoint("inspections");

    let httpParams = new HttpParams({ encoder: new CustomEncoder() });
    if (page) {
      httpParams = httpParams.set("p", page.index.toString()).set("n", page.size.toString());
    }
    if (subjectFilter && subjectFilter.subjectModelId) {
      httpParams = httpParams.set("md", subjectFilter.subjectModelId);
    }
    if (subjectFilter && subjectFilter.subjectId) {
      httpParams = httpParams.set("sb", subjectFilter.subjectId);
    }
    if (subjectFilter && subjectFilter.owner) {
      httpParams = httpParams.set("owner", subjectFilter.owner);
    }
    if (subjectFilter && subjectFilter.country) {
      httpParams = httpParams.set("country", subjectFilter.country);
    }
    if (subjectFilter && subjectFilter.state) {
      httpParams = httpParams.set("state", subjectFilter.state);
    }
    if (subjectFilter && subjectFilter.district) {
      httpParams = httpParams.set("district", subjectFilter.district);
    }
    if (subjectFilter && subjectFilter.site) {
      httpParams = httpParams.set("site", subjectFilter.site);
    }
    if (subjectFilter && subjectFilter.project) {
      httpParams = httpParams.set("project", subjectFilter.project);
    }
    if (isOwn !== null && isOwn !== undefined) {
      httpParams = httpParams.set("isOwn", String(isOwn));
    }
    if (statusCode) {
      httpParams = httpParams.set("st", statusCode);
    }
    if (sort) {
      httpParams = httpParams.set("sort", sort.active).set("sortOrder", sort.direction);
    }

    return this.http
      .get<InspectionList>(endpoint, { withCredentials: super.getWithCredentialsOption(), params: httpParams })

      .pipe(map((response) => response as InspectionList));
  }

  // IS02 GET /api/v1/inspections/{inspectionId}
  getInspection(id: string, aliveOnly?: boolean): Observable<Inspection> {
    const endpoint = super.getEndpoint("inspections", id);

    let httpParams = new HttpParams();
    if (aliveOnly !== null && aliveOnly !== undefined) {
      httpParams = httpParams.set("aliveOnly", String(aliveOnly));
    }

    return this.http
      .get<Inspection>(endpoint, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response as Inspection));
  }

  // IS09 GET /api/v1/inspections/checkedout
  getCheckedOutInspections(): Observable<Inspection[]> {
    const endpoint = super.getEndpoint("inspections", "checkedout");
    return this.http.get<Inspection[]>(endpoint, { withCredentials: super.getWithCredentialsOption() });
  }

  // IS03 POST /api/v1/inspections
  addInspection(inspection: InspectionDetailNew): Observable<Inspection> {
    const endpoint = super.getEndpoint("inspections");

    return this.http
      .post<Inspection>(endpoint, inspection, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as Inspection));
  }

  // IS04 PUT /api/v1/inspections/{inspectionId}
  updateInspection(inspection: InspectionDetailUpdate): Observable<Inspection> {
    const endpoint = super.getEndpoint("inspections", inspection.id);

    return this.http
      .put<Inspection>(endpoint, inspection, { withCredentials: super.getWithCredentialsOption(), params: new HttpParams() })
      .pipe(map((response) => response as Inspection));
  }

  // IS05 DELETE /api/v1/inspections/{inspectionId}
  deleteInspection(id: string): Observable<any> {
    const endpoint = super.getEndpoint("inspections", id);

    return this.http
      .delete(endpoint, { withCredentials: super.getWithCredentialsOption(), params: new HttpParams() })
      .pipe(map((response) => response));
  }

  // IS07 PUT /api/v1/inspections/{inspectionId}/inspectionLocations
  updateInspectionLocations(inspection: InspectionUpdateLocation): Observable<Inspection> {
    const endpoint = super.getEndpoint("inspections", inspection.inspectionId, "inspectionLocations");

    return this.http
      .put<Inspection>(endpoint, inspection, { withCredentials: super.getWithCredentialsOption(), params: new HttpParams() })
      .pipe(map((response) => response as Inspection));
  }

  // IS08 PUT /api/inspections/{inspectionId}/sharedLock
  updateInspectionSharedLock(inspForSharedLock: InspectionForSharedLock): Observable<Inspection> {
    const endpoint = super.getEndpoint("inspections", inspForSharedLock.id, "sharedLock");

    return this.http
      .put<Inspection>(endpoint, inspForSharedLock, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as Inspection));
  }
}
